/**
 * OAuth authentication service.
 *
 * Handles:
 *  1. Opening the OAuth consent screen (Google / GitHub)
 *  2. Receiving tokens from the backend callback redirect
 *  3. Persisting & refreshing tokens
 *  4. Logging out (revoking tokens)
 */

import { Capacitor } from '@capacitor/core';
import type { ApiResponse, AuthTokens, HomeBase, OAuthProvider, UserProfile } from '@/types/api';
import { setTokens, getTokens } from './api';

const TOKEN_KEY = 'auth_tokens';
const PROFILE_KEY = 'user_profile';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

function parseHomeBaseFromParams(params: URLSearchParams): HomeBase | null {
  const latRaw = params.get('home_base_lat');
  const lonRaw = params.get('home_base_lon');
  if (!latRaw || !lonRaw) return null;

  const lat = Number(latRaw);
  const lon = Number(lonRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  const labelRaw = params.get('home_base_label');
  const label = labelRaw && labelRaw.trim() ? labelRaw.trim() : undefined;

  return { lat, lon, label };
}

/**
 * Get the appropriate OAuth callback URL based on platform.
 * - Web: uses window.location.origin
 * - Android: uses custom URL scheme flighttrack://
 * - iOS: uses capacitor://localhost
 */
function getOAuthRedirectUri(): string {
  const platform = Capacitor.getPlatform();
  
  if (platform === 'android') {
    // Android uses custom URL scheme for deep linking
    return 'flighttrack://auth/callback';
  } else if (platform === 'ios') {
    // iOS uses capacitor:// scheme
    return 'capacitor://localhost/auth/callback';
  } else {
    // Web uses the current origin
    return `${window.location.origin}/auth/callback`;
  }
}

/**
 * Structural check: tokens must contain non-empty accessToken, refreshToken,
 * and a numeric expiresAt timestamp.
 */
function isValidTokenShape(tokens: unknown): tokens is AuthTokens {
  if (!tokens || typeof tokens !== 'object') return false;
  const t = tokens as Record<string, unknown>;
  return (
    typeof t.accessToken === 'string' &&
    t.accessToken.length > 0 &&
    typeof t.refreshToken === 'string' &&
    t.refreshToken.length > 0 &&
    typeof t.expiresAt === 'number' &&
    Number.isFinite(t.expiresAt)
  );
}

/** Restore persisted session on app start */
export async function restoreSession(): Promise<{
  tokens: AuthTokens | null;
  profile: UserProfile | null;
}> {
  try {
    const tokensRaw = localStorage.getItem(TOKEN_KEY);
    const profileRaw = localStorage.getItem(PROFILE_KEY);

    let tokens: AuthTokens | null = null;
    try {
      tokens = tokensRaw ? JSON.parse(tokensRaw) : null;
    } catch {
      // Corrupt JSON — treat as no session
    }

    // Guard this parse too: an unparseable profile must not throw into the outer
    // catch and discard otherwise-valid tokens, which would sign the pilot out.
    let profile: UserProfile | null = null;
    try {
      profile = profileRaw ? JSON.parse(profileRaw) : null;
    } catch {
      localStorage.removeItem(PROFILE_KEY);
    }

    if (!isValidTokenShape(tokens)) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(PROFILE_KEY);
      return { tokens: null, profile: null };
    }

    if (tokens.expiresAt > Date.now()) {
      setTokens(tokens);
    } else if (tokens.refreshToken) {
      const refreshed = await refreshAccessToken(tokens.refreshToken);
      if (refreshed) {
        const newTokens: AuthTokens = {
          accessToken: refreshed.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: refreshed.expiresAt,
        };
        setTokens(newTokens);
        localStorage.setItem(TOKEN_KEY, JSON.stringify(newTokens));
        return { tokens: newTokens, profile };
      } else {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(PROFILE_KEY);
        return { tokens: null, profile: null };
      }
    } else {
      // Expired with no refresh token
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(PROFILE_KEY);
      return { tokens: null, profile: null };
    }

    return { tokens, profile };
  } catch {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PROFILE_KEY);
    return { tokens: null, profile: null };
  }
}

/**
 * Email/password login. Returns tokens + profile; persists session locally.
 */
export async function loginWithPassword(
  email: string,
  password: string,
): Promise<{ tokens: AuthTokens; profile: UserProfile }> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const json = await res.json();

  if (!json.success || !json.data?.accessToken || !json.data?.user) {
    const message = json.error?.message ?? (res.ok ? 'Invalid response' : 'Login failed');
    throw new Error(message);
  }

  const { accessToken, refreshToken, expiresAt, user } = json.data;
  const tokens: AuthTokens = {
    accessToken,
    refreshToken,
    expiresAt: Number(expiresAt),
  };
  const profile = user as UserProfile;
  await persistSession(tokens, profile);
  return { tokens, profile };
}

/**
 * Email/password registration. Creates account and persists session locally.
 */
export async function register(params: {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}): Promise<{ tokens: AuthTokens; profile: UserProfile }> {
  const res = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  const json = await res.json();

  if (!json.success || !json.data?.accessToken || !json.data?.user) {
    const message = json.error?.message ?? (res.ok ? 'Invalid response' : 'Registration failed');
    throw new Error(message);
  }

  const { accessToken, refreshToken, expiresAt, user } = json.data;
  const tokens: AuthTokens = {
    accessToken,
    refreshToken,
    expiresAt: Number(expiresAt),
  };
  const profile = user as UserProfile;
  await persistSession(tokens, profile);
  return { tokens, profile };
}

/**
 * Kick off the OAuth flow.
 *
 * 1. Ask the backend for the provider's authorization URL
 * 2. Open it in the current window (redirect-based flow)
 *
 * After the user authorises, the backend redirects to /auth/callback
 * with token params that handleAuthCallback() picks up.
 */
export async function login(provider: OAuthProvider): Promise<{
  tokens: AuthTokens;
  profile: UserProfile;
}> {
  // Tell the backend where to redirect after OAuth completes
  const redirectUri = encodeURIComponent(getOAuthRedirectUri());

  // Get the OAuth redirect URL from our backend
  const res = await fetch(`${API_BASE}/auth/${provider}/redirect?redirect_uri=${redirectUri}`);
  const json = await res.json();

  if (!json.success || !json.data?.url) {
    throw new Error(json.error?.message ?? 'Failed to get OAuth redirect URL');
  }

  // Redirect the user to the OAuth provider
  window.location.href = json.data.url;

  // This promise never resolves because we navigate away.
  // The callback page will handle the rest.
  return new Promise(() => {});
}

/**
 * Parse the OAuth callback query params and persist the session.
 *
 * Called from the /auth/callback route in the frontend router.
 */
export async function handleAuthCallback(params: URLSearchParams): Promise<{
  tokens: AuthTokens;
  profile: UserProfile;
}> {
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  const expiresAt = params.get('expires_at');
  const userId = params.get('user_id');
  const email = params.get('email');
  const displayName = params.get('display_name');
  const avatarUrl = params.get('avatar_url');
  const provider = params.get('provider') as OAuthProvider;
  const homeBase = parseHomeBaseFromParams(params);

  if (!accessToken || !refreshToken || !expiresAt || !userId || !email) {
    throw new Error('Missing required auth callback parameters');
  }

  const tokens: AuthTokens = {
    accessToken,
    refreshToken,
    expiresAt: parseInt(expiresAt, 10),
  };

  const speedUnit = params.get('speed_unit') as UserProfile['speedUnit'] | null;
  const altitudeUnit = params.get('altitude_unit') as UserProfile['altitudeUnit'] | null;
  const distanceUnit = params.get('distance_unit') as UserProfile['distanceUnit'] | null;

  const profile: UserProfile = {
    id: userId,
    email,
    displayName: displayName ?? 'User',
    avatarUrl: avatarUrl || undefined,
    provider: provider ?? 'google',
    homeBase,
    speedUnit: speedUnit === 'kmh' || speedUnit === 'knots' || speedUnit === 'mph' ? speedUnit : undefined,
    altitudeUnit: altitudeUnit === 'm' || altitudeUnit === 'ft' ? altitudeUnit : undefined,
    distanceUnit: distanceUnit === 'km' || distanceUnit === 'nm' || distanceUnit === 'mi' ? distanceUnit : undefined,
  };

  await persistSession(tokens, profile);

  return { tokens, profile };
}

/** Refresh the access token using a refresh token */
async function refreshAccessToken(
  refreshToken: string,
): Promise<{ accessToken: string; expiresAt: number } | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    const json = await res.json();

    if (json.success && json.data?.accessToken) {
      return {
        accessToken: json.data.accessToken,
        expiresAt: json.data.expiresAt,
      };
    }

    return null;
  } catch {
    return null;
  }
}

/** Save tokens + profile locally (called after successful login) */
export async function persistSession(
  tokens: AuthTokens,
  profile: UserProfile,
): Promise<void> {
  setTokens(tokens);
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

/** Clear local session state */
export async function logout(): Promise<void> {
  // Try to revoke tokens on the backend
  const tokensRaw = localStorage.getItem(TOKEN_KEY);
  if (tokensRaw) {
    try {
      const tokens: AuthTokens = JSON.parse(tokensRaw);
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.accessToken}`,
        },
      });
    } catch {
      // Ignore errors — we're logging out anyway
    }
  }

  setTokens(null);
  localStorage.clear();
}

const PROFILE_FETCH_TIMEOUT_MS = 8_000;

export async function fetchUserProfile(): Promise<ApiResponse<UserProfile>> {
  const tokens = getTokens();

  if (!tokens?.accessToken) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PROFILE_FETCH_TIMEOUT_MS);

  try {
    const res = await fetch(`${API_BASE}/auth/user`, {
      headers: {
        Authorization: `Bearer ${tokens.accessToken}`,
      },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    const result = (await res.json()) as ApiResponse<UserProfile>;
    if (result.success && result.data) {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(result.data));
    }
    return result;
  } catch (err: any) {
    clearTimeout(timeoutId);
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: err.message ?? 'Network error' },
    };
  }
}

export async function updateHomeBase(payload: {
  label?: string | null;
  lat?: number | null;
  lon?: number | null;
  clear?: boolean;
}): Promise<ApiResponse<UserProfile>> {
  const tokens = getTokens();
  if (!tokens?.accessToken) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }

  try {
    const res = await fetch(`${API_BASE}/auth/user/home-base`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens.accessToken}`,
      },
      body: JSON.stringify(payload),
    });
    const json = (await res.json()) as ApiResponse<UserProfile>;
    if (json.success && json.data) {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(json.data));
    }
    return json;
  } catch (err: any) {
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: err.message ?? 'Network error' },
    };
  }
}

export async function updateUserUnits(payload: {
  speedUnit?: 'kmh' | 'knots' | 'mph';
  altitudeUnit?: 'm' | 'ft';
  distanceUnit?: 'km' | 'nm' | 'mi';
}): Promise<ApiResponse<UserProfile>> {
  const tokens = getTokens();
  if (!tokens?.accessToken) {
    return { success: false, error: { code: 'UNAUTHENTICATED', message: 'Not logged in' } };
  }

  const body: Record<string, string> = {};
  if (payload.speedUnit !== undefined) body.speed_unit = payload.speedUnit;
  if (payload.altitudeUnit !== undefined) body.altitude_unit = payload.altitudeUnit;
  if (payload.distanceUnit !== undefined) body.distance_unit = payload.distanceUnit;

  try {
    const res = await fetch(`${API_BASE}/auth/user/units`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens.accessToken}`,
      },
      body: JSON.stringify(body),
    });
    const json = (await res.json()) as ApiResponse<UserProfile>;
    if (json.success && json.data) {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(json.data));
    }
    return json;
  } catch (err: any) {
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: err.message ?? 'Network error' },
    };
  }
}
